import type { UiKey } from "../i18n/ui";
import type { SavingsLevers } from "../lib/savingsLevers";

type Props = {
  t: (key: UiKey) => string;
  value: SavingsLevers;
  onChange: (next: SavingsLevers) => void;
};

export function SavingsLeversPanel({ t, value, onChange }: Props) {
  function toggle<K extends keyof SavingsLevers>(key: K, checked: boolean) {
    onChange({ ...value, [key]: checked });
  }

  return (
    <div className="savings">
      <h2>{t("savings.title")}</h2>
      <p className="hint">{t("savings.hint")}</p>
      <ul className="list">
        <li>
          <label className="row">
            <input
              type="checkbox"
              checked={value.spotOrPreemptibleCompute}
              onChange={(e) => toggle("spotOrPreemptibleCompute", e.target.checked)}
            />
            <span>
              <strong>{t("savings.spot")}</strong>
              <span className="sub">{t("savings.spotHelp")}</span>
            </span>
          </label>
        </li>
        <li>
          <label className="row">
            <input
              type="checkbox"
              checked={value.dynamoCommittedCapacity}
              onChange={(e) => toggle("dynamoCommittedCapacity", e.target.checked)}
            />
            <span>
              <strong>{t("savings.dynamo")}</strong>
              <span className="sub">{t("savings.dynamoHelp")}</span>
            </span>
          </label>
        </li>
        <li>
          <label className="row">
            <input
              type="checkbox"
              checked={value.llmEconomyTier}
              onChange={(e) => toggle("llmEconomyTier", e.target.checked)}
            />
            <span>
              <strong>{t("savings.llm")}</strong>
              <span className="sub">{t("savings.llmHelp")}</span>
            </span>
          </label>
        </li>
        <li>
          <label className="row">
            <input
              type="checkbox"
              checked={value.llmBatchApi}
              onChange={(e) => toggle("llmBatchApi", e.target.checked)}
            />
            <span>
              <strong>{t("savings.llmBatch")}</strong>
              <span className="sub">{t("savings.llmBatchHelp")}</span>
            </span>
          </label>
        </li>
        <li>
          <label className="row">
            <input
              type="checkbox"
              checked={value.scheduleOffWeekends}
              onChange={(e) => toggle("scheduleOffWeekends", e.target.checked)}
            />
            <span>
              <strong>{t("savings.weekends")}</strong>
              <span className="sub">{t("savings.weekendsHelp")}</span>
            </span>
          </label>
        </li>
      </ul>
      <style>{`
        .savings h2 {
          font-size: 1rem;
          margin: 0 0 0.35rem;
        }
        .hint {
          margin: 0 0 0.85rem;
          font-size: 0.82rem;
          color: var(--muted);
        }
        .list {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .list li {
          padding: 0.45rem 0;
          border-bottom: 1px solid var(--border);
        }
        .list li:last-child {
          border-bottom: none;
        }
        .row {
          display: flex;
          align-items: flex-start;
          gap: 0.55rem;
          cursor: pointer;
        }
        .row input {
          margin-top: 0.2rem;
          accent-color: var(--accent);
        }
        .row > span {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
          font-size: 0.88rem;
        }
        .row strong {
          font-weight: 600;
          color: var(--text);
        }
        .sub {
          font-size: 0.78rem;
          color: var(--muted);
          line-height: 1.35;
        }
      `}</style>
    </div>
  );
}
